import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  ImageIcon,
  ImagePlus,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Palette,
  Redo2,
  Strikethrough,
  Table as TableIcon,
  Underline,
  Undo2,
  Video,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';
import { Toggle } from '@nucleus-commerce/ui/components/toggle';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@nucleus-commerce/ui/components/tooltip';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { AltTextDialog, LinkDialog, VideoDialog, type LinkValue } from './rich-text-dialogs';
import { richTextExtensions } from './rich-text-extensions';

export interface PickedImage {
  src: string;
  alt: string | null;
}

export interface RichTextEditorProps {
  /** HTML as stored by the API; null or '' is an empty document. */
  value: string | null;
  /** Receives the editor's HTML, or null when the document is empty. */
  onChange: (html: string | null) => void;
  onBlur?: (() => void) | undefined;
  /**
   * Opens the host app's image picker. Resolves with the chosen image, or null when cancelled.
   * Without it the toolbar has no image button.
   */
  onPickImage?: (() => Promise<PickedImage | null>) | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  /** Shows the content without a toolbar and refuses edits. */
  readOnly?: boolean | undefined;
  id?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
  className?: string | undefined;
}

const BLOCK_HTML =
  /(<\/(?:p|h[1-6]|blockquote|ul|ol|li|table|thead|tbody|tr|pre)>|<hr>|<\/iframe>|<img [^>]*>)(?!\n)/g;

/** One block per line so the source view is readable; the parser ignores the added whitespace. */
function formatHtmlSource(html: string): string {
  return html.replace(BLOCK_HTML, '$1\n').trim();
}

// The trailing-node plugin keeps an empty paragraph after a final table or image so the cursor
// can get below it; it is editing scaffolding, not content.
const TRAILING_EMPTY_PARAGRAPHS = /(?:<p><\/p>)+$/;

function editorHtml(editor: Editor): string | null {
  if (editor.isEmpty) return null;
  return orNull(editor.getHTML().replace(TRAILING_EMPTY_PARAGRAPHS, ''));
}

function orNull(value: string | null | undefined): string | null {
  return value === undefined || value === '' ? null : value;
}

function sameValue(a: string | null, b: string | null): boolean {
  return orNull(a) === orNull(b);
}

const PALETTE = [
  '#000000',
  '#4b5563',
  '#9ca3af',
  '#dc2626',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#0d9488',
  '#2563eb',
  '#4f46e5',
  '#9333ea',
  '#db2777',
] as const;

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

const BLOCK_STYLES = ['paragraph', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'quote'] as const;
type BlockStyle = (typeof BLOCK_STYLES)[number];
const OFFERED_BLOCK_STYLES: readonly BlockStyle[] = ['paragraph', 'h2', 'h3', 'h4', 'quote'];

const ALIGNMENTS = ['left', 'center', 'right', 'justify'] as const;
type Alignment = (typeof ALIGNMENTS)[number];
const ALIGN_ICON: Record<Alignment, LucideIcon> = {
  left: AlignLeft,
  center: AlignCenter,
  right: AlignRight,
  justify: AlignJustify,
};

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);

function shortcut(keys: string): string {
  return keys.replace('Mod', IS_MAC ? '⌘' : 'Ctrl').replace('Alt', IS_MAC ? '⌥' : 'Alt');
}

const CONTENT_CLASS = cn(
  'min-h-[220px] px-3 py-2 text-sm leading-relaxed outline-none',
  '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-2xl [&_h1]:font-semibold',
  '[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-xl [&_h2]:font-semibold',
  '[&_h3]:mt-3 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold',
  '[&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:text-base [&_h4]:font-semibold',
  '[&_h5]:mt-3 [&_h5]:mb-1 [&_h5]:font-semibold [&_h6]:mt-3 [&_h6]:mb-1 [&_h6]:font-semibold',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6',
  '[&_blockquote]:border-l-4 [&_blockquote]:pl-3 [&_blockquote]:italic [&_li_p]:my-0.5',
  '[&_a]:text-primary [&_a]:underline [&_hr]:my-4',
  '[&_pre]:bg-muted [&_code]:font-mono [&_pre]:rounded-md [&_pre]:p-3 [&_pre]:font-mono',
  '[&_img]:my-2 [&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-md',
  '[&_img.ProseMirror-selectednode]:ring-ring [&_img.ProseMirror-selectednode]:ring-2',
  '[&_table]:my-2 [&_table]:w-full [&_table]:table-fixed [&_table]:border-collapse',
  '[&_td]:border [&_td]:px-2 [&_td]:py-1 [&_td]:align-top [&_th]:border [&_th]:px-2 [&_th]:py-1',
  '[&_th]:bg-muted [&_.selectedCell]:bg-accent [&_th]:text-left [&_th]:font-semibold',
  '[&_.rte-video]:my-2 [&_.rte-video_iframe]:aspect-video [&_.rte-video_iframe]:h-auto [&_.rte-video_iframe]:w-full [&_.rte-video_iframe]:max-w-xl',
  '[&_.rte-video.ProseMirror-selectednode]:ring-ring [&_.rte-video.ProseMirror-selectednode]:ring-2',
);

/**
 * WYSIWYG editor over HTML. Its schema only produces markup the API's sanitiser keeps: alignment
 * and colour as inline `style`, plain tables, and iframes from YouTube or Vimeo. Markup outside
 * the schema is dropped once the document is edited in the visual view.
 */
export function RichTextEditor({
  value,
  onChange,
  onBlur,
  onPickImage,
  placeholder,
  disabled = false,
  readOnly = false,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  className,
}: RichTextEditorProps) {
  const { t } = useTranslation('common');
  const editable = !disabled && !readOnly;

  // The last HTML this component emitted or adopted; a prop that differs came from outside.
  const lastValue = useRef<string | null>(orNull(value));
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  const openLinkRef = useRef<() => void>(() => undefined);
  // Until the editor emits, the prop is whatever the API stored; the source view shows it verbatim.
  const valueIsExternal = useRef(true);
  const sourceRef = useRef<HTMLTextAreaElement>(null);
  const sourceEdited = useRef(false);
  const [mode, setMode] = useState<'visual' | 'html'>('visual');
  const [sourceText, setSourceText] = useState('');

  const attributes = useMemo(
    () => ({
      role: 'textbox',
      'aria-multiline': 'true',
      ...(id ? { id } : {}),
      ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
      ...(ariaInvalid ? { 'aria-invalid': 'true' } : {}),
      ...(editable ? {} : { 'aria-readonly': 'true' }),
      class: CONTENT_CLASS,
    }),
    [id, ariaLabel, ariaDescribedBy, ariaInvalid, editable],
  );
  const attributesRef = useRef(attributes);

  const editor = useEditor({
    extensions: richTextExtensions(),
    content: value ?? '',
    editable,
    editorProps: {
      attributes: () => attributesRef.current,
      handleKeyDown: (_view, event) => {
        if (
          (event.metaKey || event.ctrlKey) &&
          !event.shiftKey &&
          event.key.toLowerCase() === 'k'
        ) {
          event.preventDefault();
          openLinkRef.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: current }) => {
      const html = editorHtml(current);
      lastValue.current = html;
      valueIsExternal.current = false;
      onChangeRef.current(html);
    },
    onBlur: () => onBlurRef.current?.(),
  });

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
    onBlurRef.current = onBlur;
  });

  useEffect(() => {
    editor.setEditable(editable, false);
  }, [editor, editable]);

  useEffect(() => {
    attributesRef.current = attributes;
    if (!editor.isDestroyed) editor.view.setProps({});
  }, [editor, attributes]);

  useEffect(() => {
    if (sameValue(value, lastValue.current)) return;
    lastValue.current = orNull(value);
    valueIsExternal.current = true;
    editor.commands.setContent(value ?? '', { emitUpdate: false });
    // A reset while the source view is open replaces the text the user sees.
    if (sourceRef.current) {
      sourceRef.current.value = value ?? '';
      sourceEdited.current = false;
    }
  }, [editor, value]);

  const emit = (html: string | null) => {
    lastValue.current = html;
    valueIsExternal.current = false;
    onChangeRef.current(html);
  };

  const showSource = () => {
    const current = valueIsExternal.current ? lastValue.current : editorHtml(editor);
    setSourceText(formatHtmlSource(current ?? ''));
    sourceEdited.current = false;
    setMode('html');
  };

  const showVisual = () => {
    if (sourceEdited.current && sourceRef.current) {
      editor.commands.setContent(sourceRef.current.value, { emitUpdate: false });
      emit(editorHtml(editor));
    }
    setMode('visual');
  };

  return (
    <div
      className={cn(
        'border-input bg-background dark:bg-input/30 relative flex flex-col rounded-md border shadow-xs transition-[color,box-shadow]',
        'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
        ariaInvalid && 'border-destructive ring-destructive/20 dark:ring-destructive/40',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
      data-slot="rich-text-editor"
    >
      {readOnly ? null : (
        <Toolbar
          editor={editor}
          disabled={disabled}
          sourceMode={mode === 'html'}
          onToggleSource={() => (mode === 'html' ? showVisual() : showSource())}
          onPickImage={onPickImage}
          openLinkRef={openLinkRef}
        />
      )}
      {mode === 'html' ? (
        <Textarea
          ref={sourceRef}
          defaultValue={sourceText}
          disabled={disabled}
          spellCheck={false}
          aria-label={t('richText.sourceLabel')}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className="field-sizing-content max-h-[600px] min-h-[220px] resize-y rounded-none rounded-b-md border-0 font-mono text-[13px] shadow-none focus-visible:ring-0"
          onChange={(event) => {
            sourceEdited.current = true;
            emit(event.target.value.trim() ? event.target.value : null);
          }}
          onBlur={() => onBlur?.()}
        />
      ) : null}
      {/* Hidden rather than unmounted in source mode so the editor view and its history survive. */}
      <div className={cn('relative max-h-[600px] overflow-y-auto', mode === 'html' && 'hidden')}>
        <EditorContent editor={editor} />
        {placeholder ? <Placeholder editor={editor}>{placeholder}</Placeholder> : null}
      </div>
    </div>
  );
}

function Placeholder({ editor, children }: { editor: Editor; children: ReactNode }) {
  const isEmpty = useEditorState({ editor, selector: ({ editor: e }) => e.isEmpty });
  if (!isEmpty) return null;
  return (
    <p
      className="text-muted-foreground pointer-events-none absolute top-2 right-3 left-3 truncate py-2 text-sm"
      aria-hidden
    >
      {children}
    </p>
  );
}

interface ToolbarProps {
  editor: Editor;
  disabled: boolean;
  sourceMode: boolean;
  onToggleSource: () => void;
  onPickImage: (() => Promise<PickedImage | null>) | undefined;
  openLinkRef: { current: () => void };
}

function blockStyleOf(editor: Editor): BlockStyle {
  for (const level of [1, 2, 3, 4, 5, 6] as const) {
    if (editor.isActive('heading', { level })) return `h${level}`;
  }
  if (editor.isActive('blockquote')) return 'quote';
  return 'paragraph';
}

function alignmentOf(editor: Editor): Alignment {
  return ALIGNMENTS.find((align) => editor.isActive({ textAlign: align })) ?? 'left';
}

function Toolbar({
  editor,
  disabled,
  sourceMode,
  onToggleSource,
  onPickImage,
  openLinkRef,
}: ToolbarProps) {
  const { t } = useTranslation('common');
  const off = disabled || sourceMode;
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      block: blockStyleOf(e),
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      color: (e.getAttributes('textStyle').color as string | undefined) ?? null,
      align: alignmentOf(e),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      link: e.isActive('link'),
      image: e.isActive('image'),
      table: e.isActive('table'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkInitial, setLinkInitial] = useState<LinkValue | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [altOpen, setAltOpen] = useState(false);

  const openLink = () => {
    if (off) return;
    const attrs = editor.getAttributes('link') as { href?: string; target?: string | null };
    setLinkInitial(attrs.href ? { href: attrs.href, newTab: attrs.target === '_blank' } : null);
    setLinkOpen(true);
  };
  useLayoutEffect(() => {
    openLinkRef.current = openLink;
  });

  const applyLink = ({ href, newTab }: LinkValue) => {
    setLinkOpen(false);
    const attrs = {
      href,
      target: newTab ? '_blank' : null,
      rel: newTab ? 'noopener noreferrer' : null,
    };
    const chain = editor.chain().focus().extendMarkRange('link');
    if (editor.state.selection.empty && !editor.isActive('link')) {
      chain.insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs }] }).run();
    } else {
      chain.setLink(attrs).run();
    }
  };

  const pickImage = async () => {
    if (!onPickImage) return;
    const picked = await onPickImage();
    if (!picked) return;
    editor
      .chain()
      .focus()
      .setImage({ src: picked.src, ...(picked.alt ? { alt: picked.alt } : {}) })
      .run();
  };

  const setBlock = (style: BlockStyle) => {
    const chain = editor.chain().focus();
    if (style === 'quote') {
      if (!editor.isActive('blockquote')) chain.setParagraph().setBlockquote();
      chain.run();
      return;
    }
    if (editor.isActive('blockquote')) chain.unsetBlockquote();
    if (style === 'paragraph') chain.setParagraph();
    else chain.setHeading({ level: Number(style.slice(1)) as 1 | 2 | 3 | 4 | 5 | 6 });
    chain.run();
  };

  const AlignIcon = ALIGN_ICON[state.align];
  // Menus hand focus back to their trigger on close; the caret belongs in the document.
  const returnFocus = (event: Event) => {
    event.preventDefault();
    editor.commands.focus();
  };

  return (
    <TooltipProvider delayDuration={400}>
      <div
        role="toolbar"
        aria-label={t('richText.toolbar')}
        aria-orientation="horizontal"
        className="bg-muted/40 flex flex-wrap items-center gap-0.5 rounded-t-md border-b p-1"
      >
        <Select
          value={state.block}
          onValueChange={(next) => setBlock(next as BlockStyle)}
          disabled={off}
        >
          <SelectTrigger
            size="sm"
            className="h-8 w-36 border-0 bg-transparent shadow-none"
            aria-label={t('richText.blockStyle')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent onCloseAutoFocus={returnFocus}>
            {BLOCK_STYLES.filter(
              (style) => OFFERED_BLOCK_STYLES.includes(style) || style === state.block,
            ).map((style) => (
              <SelectItem key={style} value={style}>
                {t(`richText.blocks.${style}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Divider />
        <ToolToggle
          icon={Bold}
          label={t('richText.bold')}
          keys="Mod+B"
          pressed={state.bold}
          disabled={off}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolToggle
          icon={Italic}
          label={t('richText.italic')}
          keys="Mod+I"
          pressed={state.italic}
          disabled={off}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolToggle
          icon={Underline}
          label={t('richText.underline')}
          keys="Mod+U"
          pressed={state.underline}
          disabled={off}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <ToolToggle
          icon={Strikethrough}
          label={t('richText.strike')}
          keys="Mod+Shift+S"
          pressed={state.strike}
          disabled={off}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
        <ColorPicker
          color={state.color}
          disabled={off}
          onPick={(color) => editor.chain().focus().setColor(color).run()}
          onClear={() => editor.chain().focus().unsetColor().run()}
        />
        <Divider />
        <DropdownMenu modal={false}>
          <ToolTip label={t('richText.align.label')}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={off}
                aria-label={t('richText.align.label')}
              >
                <AlignIcon aria-hidden />
              </Button>
            </DropdownMenuTrigger>
          </ToolTip>
          <DropdownMenuContent align="start" onCloseAutoFocus={returnFocus}>
            <DropdownMenuRadioGroup
              value={state.align}
              onValueChange={(align) => editor.chain().focus().setTextAlign(align).run()}
            >
              {ALIGNMENTS.map((align) => {
                const Icon = ALIGN_ICON[align];
                return (
                  <DropdownMenuRadioItem key={align} value={align}>
                    <Icon aria-hidden />
                    {t(`richText.align.${align}`)}
                  </DropdownMenuRadioItem>
                );
              })}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <ToolToggle
          icon={List}
          label={t('richText.bulletList')}
          keys="Mod+Shift+8"
          pressed={state.bulletList}
          disabled={off}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolToggle
          icon={ListOrdered}
          label={t('richText.orderedList')}
          keys="Mod+Shift+7"
          pressed={state.orderedList}
          disabled={off}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <Divider />
        <ToolToggle
          icon={LinkIcon}
          label={t('richText.link.button')}
          keys="Mod+K"
          pressed={state.link}
          disabled={off}
          onClick={openLink}
        />
        {onPickImage ? (
          <ToolButton
            icon={ImagePlus}
            label={t('richText.image.insert')}
            disabled={off}
            onClick={() => void pickImage()}
          />
        ) : null}
        {state.image ? (
          <ToolButton
            icon={ImageIcon}
            label={t('richText.image.altButton')}
            disabled={off}
            onClick={() => setAltOpen(true)}
          />
        ) : null}
        <ToolButton
          icon={Video}
          label={t('richText.video.button')}
          disabled={off}
          onClick={() => setVideoOpen(true)}
        />
        <DropdownMenu modal={false}>
          <ToolTip label={t('richText.table.label')}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={off}
                aria-label={t('richText.table.label')}
              >
                <TableIcon aria-hidden />
              </Button>
            </DropdownMenuTrigger>
          </ToolTip>
          <DropdownMenuContent align="start" onCloseAutoFocus={returnFocus}>
            <DropdownMenuItem
              disabled={state.table}
              onSelect={() =>
                editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
              }
            >
              {t('richText.table.insert')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!state.table}
              onSelect={() => editor.chain().focus().addRowBefore().run()}
            >
              {t('richText.table.addRowBefore')}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!state.table}
              onSelect={() => editor.chain().focus().addRowAfter().run()}
            >
              {t('richText.table.addRowAfter')}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!state.table}
              onSelect={() => editor.chain().focus().deleteRow().run()}
            >
              {t('richText.table.deleteRow')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!state.table}
              onSelect={() => editor.chain().focus().addColumnBefore().run()}
            >
              {t('richText.table.addColumnBefore')}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!state.table}
              onSelect={() => editor.chain().focus().addColumnAfter().run()}
            >
              {t('richText.table.addColumnAfter')}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!state.table}
              onSelect={() => editor.chain().focus().deleteColumn().run()}
            >
              {t('richText.table.deleteColumn')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              disabled={!state.table}
              onSelect={() => editor.chain().focus().deleteTable().run()}
            >
              {t('richText.table.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ToolButton
          icon={Minus}
          label={t('richText.horizontalRule')}
          disabled={off}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        />
        <Divider />
        <ToolButton
          icon={Undo2}
          label={t('richText.undo')}
          keys="Mod+Z"
          disabled={off || !state.canUndo}
          onClick={() => editor.chain().focus().undo().run()}
        />
        <ToolButton
          icon={Redo2}
          label={t('richText.redo')}
          keys="Mod+Shift+Z"
          disabled={off || !state.canRedo}
          onClick={() => editor.chain().focus().redo().run()}
        />
        <div className="ml-auto">
          <ToolToggle
            icon={Code}
            label={t('richText.showHtml')}
            pressed={sourceMode}
            disabled={disabled}
            onClick={onToggleSource}
          />
        </div>
      </div>
      <LinkDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        initial={linkInitial}
        onSubmit={applyLink}
        onRemove={() => {
          setLinkOpen(false);
          editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }}
      />
      <VideoDialog
        open={videoOpen}
        onOpenChange={setVideoOpen}
        onSubmit={(src) => {
          setVideoOpen(false);
          editor.chain().focus().setVideoEmbed({ src }).run();
        }}
      />
      <AltTextDialog
        open={altOpen}
        onOpenChange={setAltOpen}
        initial={(editor.getAttributes('image').alt as string | undefined) ?? ''}
        onSubmit={(alt) => {
          setAltOpen(false);
          editor
            .chain()
            .focus()
            .updateAttributes('image', { alt: alt || null })
            .run();
        }}
      />
    </TooltipProvider>
  );
}

function Divider() {
  return <span className="bg-border mx-0.5 h-5 w-px" aria-hidden />;
}

function ToolTip({
  label,
  keys,
  children,
}: {
  label: string;
  keys?: string | undefined;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>
        {label}
        {keys ? <span className="ml-2 opacity-70">{shortcut(keys)}</span> : null}
      </TooltipContent>
    </Tooltip>
  );
}

interface ToolProps {
  icon: LucideIcon;
  label: string;
  keys?: string | undefined;
  disabled: boolean;
  onClick: () => void;
}

function ToolToggle({
  icon: Icon,
  label,
  keys,
  pressed,
  disabled,
  onClick,
}: ToolProps & { pressed: boolean }) {
  return (
    <ToolTip label={label} keys={keys}>
      <Toggle
        size="sm"
        pressed={pressed}
        onPressedChange={onClick}
        aria-label={label}
        disabled={disabled}
        onMouseDown={(event) => event.preventDefault()}
      >
        <Icon className="size-4" aria-hidden />
      </Toggle>
    </ToolTip>
  );
}

function ToolButton({ icon: Icon, label, keys, disabled, onClick }: ToolProps) {
  return (
    <ToolTip label={label} keys={keys}>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={label}
        disabled={disabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClick}
      >
        <Icon aria-hidden />
      </Button>
    </ToolTip>
  );
}

interface ColorPickerProps {
  color: string | null;
  disabled: boolean;
  onPick: (color: string) => void;
  onClear: () => void;
}

function ColorPicker({ color, disabled, onPick, onClear }: ColorPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const customValid = HEX_COLOR.test(custom.trim());

  const pick = (next: string) => {
    onPick(next.toLowerCase());
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setCustom(color ?? '');
      }}
    >
      <ToolTip label={t('richText.color.label')}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={disabled}
            aria-label={t('richText.color.label')}
          >
            <span className="relative">
              <Palette aria-hidden className="size-4" />
              <span
                className="absolute -bottom-1 left-0 h-0.5 w-full rounded-full"
                style={{ backgroundColor: color ?? 'currentColor' }}
                aria-hidden
              />
            </span>
          </Button>
        </PopoverTrigger>
      </ToolTip>
      <PopoverContent align="start" className="w-60">
        <div
          className="grid grid-cols-6 gap-1.5"
          role="group"
          aria-label={t('richText.color.palette')}
        >
          {PALETTE.map((swatch) => (
            <button
              key={swatch}
              type="button"
              className={cn(
                'focus-visible:ring-ring size-7 rounded-md border outline-none focus-visible:ring-2',
                color === swatch && 'ring-ring ring-2 ring-offset-1',
              )}
              style={{ backgroundColor: swatch }}
              aria-label={t('richText.color.swatch', { color: swatch })}
              aria-pressed={color === swatch}
              onClick={() => pick(swatch)}
            />
          ))}
        </div>
        <form
          className="mt-3 flex gap-2"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (customValid) pick(custom.trim());
          }}
        >
          <Input
            value={custom}
            placeholder="#1f2937"
            aria-label={t('richText.color.custom')}
            aria-invalid={custom.trim() !== '' && !customValid}
            className="h-8 font-mono text-xs"
            onChange={(event) => setCustom(event.target.value)}
          />
          <Button type="submit" size="sm" variant="outline" disabled={!customValid}>
            {t('richText.apply')}
          </Button>
        </form>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2 w-full"
          disabled={!color}
          onClick={() => {
            onClear();
            setOpen(false);
          }}
        >
          {t('richText.color.clear')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
